import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Badge,
  Skeleton,
  Animate,
  EmptyState,
  IconButton,
  Select
} from 'wss3-forge'
import {
  History20Regular,
  ArrowDownload20Regular,
  Checkmark20Regular,
  Clock20Regular,
  Dismiss20Regular,
  ArrowUndo20Regular
} from '@fluentui/react-icons'

interface Transaction {
  id: string
  amount: number
  currency: string
  status: 'completed' | 'pending' | 'failed' | 'refunded'
  description: string
  created_at: string
  invoice_url?: string
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' }
]

export function Transactions() {
  const { user } = useAuth()
  usePageTitle('Transactions')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (user && !initialLoadDone.current) {
      loadTransactions()
    }
  }, [user])

  const loadTransactions = async () => {
    if (!user) return

    setLoading(true)
    // TODO: Implement transactions table query when Stripe is set up
    setTransactions([])
    initialLoadDone.current = true
    setLoading(false)
  }

  const getStatusIcon = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return <Checkmark20Regular style={{ color: 'var(--color-success)' }} />
      case 'pending':
        return <Clock20Regular style={{ color: 'var(--color-warning)' }} />
      case 'failed':
        return <Dismiss20Regular style={{ color: 'var(--color-error)' }} />
      case 'refunded':
        return <ArrowUndo20Regular style={{ color: 'var(--text-muted)' }} />
    }
  }

  const getStatusVariant = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'error'
      case 'refunded':
        return 'default'
    }
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100)
  }

  const filteredTransactions = transactions.filter(t =>
    statusFilter === 'all' || t.status === statusFilter
  )

  return (
    <div className="page-content">
      <VStack gap="md">
        <Animate type="fadeIn">
          <HStack style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <VStack gap="xs">
              <Heading level={2}>Transactions</Heading>
              <Text color="muted">View your payment history</Text>
            </VStack>
            <div style={{ width: '250px' }}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />
            </div>
          </HStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          {loading ? (
            <VStack gap="sm">
              <Card padding="md">
                <HStack gap="md" style={{ alignItems: 'center' }}>
                  <Skeleton width={24} height={24} />
                  <VStack gap="xs" style={{ flex: 1 }}>
                    <Skeleton width={180} height={20} />
                    <Skeleton width={120} height={16} />
                  </VStack>
                  <Skeleton width={80} height={24} />
                </HStack>
              </Card>
            </VStack>
          ) : filteredTransactions.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                icon={<History20Regular />}
                title={statusFilter === 'all' ? "No transactions yet" : "No matching transactions"}
                description={statusFilter === 'all' ? "Your payment history will appear here" : "Try changing the filter"}
              />
            </Card>
          ) : (
            <VStack gap="sm">
              {filteredTransactions.map((transaction) => (
                <Card key={transaction.id} padding="md">
                  <HStack gap="md" style={{ alignItems: 'center' }}>
                    {getStatusIcon(transaction.status)}
                    <VStack gap="none" style={{ flex: 1 }}>
                      <Text weight="medium">{transaction.description}</Text>
                      <Text size="sm" color="muted">
                        {new Date(transaction.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Text>
                    </VStack>
                    <VStack gap="xs" style={{ alignItems: 'flex-end' }}>
                      <Text weight="medium">
                        {formatAmount(transaction.amount, transaction.currency)}
                      </Text>
                      <Badge variant={getStatusVariant(transaction.status)} size="md">
                        {transaction.status}
                      </Badge>
                    </VStack>
                    {transaction.invoice_url && (
                      <IconButton
                        icon={<ArrowDownload20Regular />}
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(transaction.invoice_url, '_blank')}
                        aria-label="Download invoice"
                      />
                    )}
                  </HStack>
                </Card>
              ))}
            </VStack>
          )}
        </Animate>
      </VStack>
    </div>
  )
}
